import { forwardRef, type ButtonHTMLAttributes } from "react";

type ToggleChipSize = "xs" | "sm" | "md";
export type ToggleChipVariant = "filter" | "pill";

export type ToggleChipButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  active: boolean;
  size?: ToggleChipSize;
  /**
   * Visual variant. Defaults to `"filter"`, which preserves the original
   * chip-button look used in Orders / Products filter rows.
   *
   * `"pill"` is reserved for Settings-style tab treatments per the
   * user-sanctioned contract exception. Do NOT use `"pill"` for generic
   * chip/filter contexts — see UX Interaction Contract v1 §Tab Navigation.
   * When `variant="pill"`, the `size` prop is ignored (pill is always `md`).
   */
  variant?: ToggleChipVariant;
};

const sizeClasses: Record<ToggleChipSize, string> = {
  xs: "px-3 py-1 text-[13px]",
  sm: "px-4 py-2 text-xs",
  md: "px-4 py-2 text-sm",
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export const ToggleChipButton = forwardRef<
  HTMLButtonElement,
  ToggleChipButtonProps
>(
  (
    {
      type = "button",
      active,
      size = "sm",
      variant = "filter",
      className,
      ...props
    },
    ref,
  ) => {
    if (variant === "pill") {
      return (
        <button
          ref={ref}
          type={type}
          className={cx(
            "h-10 whitespace-nowrap rounded-full border px-4 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            active
              ? "border-[color:var(--primary-500)] bg-[color:var(--primary-50)] text-[color:var(--primary-600)] ring-1 ring-[color:var(--primary-500)]"
              : "border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-muted)] hover:border-[color:var(--border-strong)] hover:text-[color:var(--text-secondary)]",
            className,
          )}
          {...props}
        />
      );
    }

    return (
      <button
        ref={ref}
        type={type}
        className={cx(
          "whitespace-nowrap rounded-full border font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
          sizeClasses[size],
          active
            ? "border-[color:var(--border-strong)] bg-[color:var(--surface)] text-[color:var(--text-primary)]"
            : "border-[color:var(--border-light)] bg-[color:var(--background)] text-[color:var(--text-muted)] hover:border-[color:var(--border-strong)]",
          className,
        )}
        {...props}
      />
    );
  },
);

ToggleChipButton.displayName = "ToggleChipButton";
